/**
 * Singleton top-level Redux store. You can interact with the store using `getState`, `dispatch`,
 * and `subscribe`, but generally code should instead use `connect` from the react-redux package.
 *
 * http://redux.js.org/docs/api/Store.html
 * http://redux.js.org/docs/basics/UsageWithReact.html
 */
import {applyMiddleware, createStore, Reducer, Unsubscribe} from 'redux';
import {composeWithDevTools} from 'redux-devtools-extension/logOnlyInProduction';
import {thunk, ThunkAction, ThunkDispatch, ThunkMiddleware} from 'redux-thunk';

import {captureActionsInSentryReduxMiddleware} from 'ErrorConfig';
import {captureActionsForDebuggingReduxMiddleware} from 'graphql-client/sync/SyncerConsoleDebug';
import lazyInit from 'util/lazyInit';

import {BenchlingAction} from './Action';
import reduceApp, {BenchlingState} from './reduceApp';

export type Thunk<T> = ThunkAction<T, BenchlingState, unknown, BenchlingAction>;
export type BenchlingDispatch = ThunkDispatch<BenchlingState, unknown, BenchlingAction>;

/**
 * Our own store interface, more specific than the built-in store type.
 */
export interface BenchlingStore {
  dispatch<T = void>(action: BenchlingAction | Thunk<T>): T;
  getState(): BenchlingState;
  subscribe(listener: () => void): Unsubscribe;
}

export const getStore = lazyInit<BenchlingStore>(() =>
  createStore(
    // This is only accessed as part of lazyInit with applies the BenchlingStore type.
    // Our types are a bit disconnected from the new typing system of Redux, so we need to cast here.
    // createStore is now deprecated and we should switch to configureStore from redux-toolkit which has
    // a better typing system.
    reduceApp as unknown as Reducer<BenchlingState>,
    // Middlewares, like `redux-thunk` that intercept or emit actions should
    // precede `captureActionsInSentryReduxMiddleware `.
    composeWithDevTools(
      applyMiddleware<ThunkMiddleware<BenchlingState, BenchlingAction>>(
        thunk,
        captureActionsInSentryReduxMiddleware,
        captureActionsForDebuggingReduxMiddleware
      )
    )
  )
);
